import type { Player } from '../types/player';
import { 
  DEFAULT_SCORING
} from '../types/draft';
import type { 
  LeagueSettings, 
  DraftState, 
  DraftRecommendation, 
  DraftStrategy,
  MockDraftResult
  // Removed unused CustomScoringRules import
} from '../types/draft';
import { calculateOverallPlayerScore } from './playerValue';

/**
 * Fantasy Draft Assistant class that provides draft recommendations based on
 * league settings, scoring formats, and team needs
 */
export class FantasyDraftAssistant {
  private leagueSettings: LeagueSettings;
  // Remove unused scoringRules variable
  private playerDatabase: Player[];
  private valueBasedDrafting: Map<string, number> = new Map();
  private playerTiers: Map<string, number> = new Map();
  
  // Helper method to ensure player ID is always a string when used as a Map key
  private getPlayerIdAsString(id: string | number): string {
    return String(id);
  }

  constructor(leagueSettings: LeagueSettings, playerDatabase: Player[]) {
    this.leagueSettings = leagueSettings;
    // Remove reference to unused scoringRules
    this.playerDatabase = playerDatabase;
    this.calculateVBD();
    this.assignTiers();
  }

  /**
   * Calculate Value-Based Drafting scores for all players
   */
  private calculateVBD(): void {
    const positionBaselines = this.calculatePositionBaselines();
    
    this.playerDatabase.forEach(player => {
      // Use our existing player valuation formula
      const replacementLevel = positionBaselines.get(player.position) || 0;
      
      try {
        // Use the proprietary valuation formula
        const { overallScore } = calculateOverallPlayerScore(
          player, 
          { 
            leagueSize: this.leagueSettings.teams, 
            startingSlots: this.convertRosterConfigToStartingSlots() 
          },
          [], // Empty roster for baseline calculations
          replacementLevel
        );
        
        this.valueBasedDrafting.set(this.getPlayerIdAsString(player.id), overallScore);
      } catch (error) {
        console.error(`Error calculating VBD for ${player.name}:`, error);
        // Fallback to a simple projection-based value
        this.valueBasedDrafting.set(this.getPlayerIdAsString(player.id), player.projection || 0);
      }
    });
  }

  /**
   * Convert our roster configuration to the format expected by the valuation formula
   */
  private convertRosterConfigToStartingSlots(): Record<string, number> {
    const { rosterPositions, flexPositions } = this.leagueSettings;
    
    return {
      QB: rosterPositions.qb + (flexPositions.qbRbWrTe || 0),
      RB: rosterPositions.rb,
      WR: rosterPositions.wr,
      TE: rosterPositions.te,
      K: rosterPositions.k,
      DEF: rosterPositions.def,
      FLEX: flexPositions.rbWrTe + flexPositions.rbWr + flexPositions.wrTe
    };
  }

  /**
   * Calculate baseline scores for each position (replacement level)
   */
  private calculatePositionBaselines(): Map<string, number> {
    const baselines = new Map<string, number>();
    const positions = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];
    
    positions.forEach(position => {
      const positionPlayers = this.playerDatabase
        .filter(p => p.position === position)
        .sort((a, b) => (b.projection || 0) - (a.projection || 0));
      
      // Baseline is typically the last starter + some bench depth
      const startersNeeded = this.getStartersNeededByPosition(position);
      const totalNeeded = startersNeeded * this.leagueSettings.teams;
      const benchDepth = Math.floor(totalNeeded * 0.5); // 50% bench depth
      const baselineIndex = totalNeeded + benchDepth - 1;
      
      if (positionPlayers[baselineIndex]) {
        baselines.set(position, positionPlayers[baselineIndex].projection || 0);
      } else {
        baselines.set(position, 0);
      }
    });
    
    return baselines;
  }

  /**
   * Get number of starters needed for each position
   */
  private getStartersNeededByPosition(position: string): number {
    const roster = this.leagueSettings.rosterPositions;
    const flex = this.leagueSettings.flexPositions;
    
    switch (position) {
      case 'QB':
        return roster.qb + (flex.qbRbWrTe || 0);
      case 'RB':
        return roster.rb + flex.rbWrTe + flex.rbWr;
      case 'WR':
        return roster.wr + flex.rbWrTe + flex.rbWr + flex.wrTe;
      case 'TE':
        return roster.te + flex.rbWrTe + flex.wrTe;
      case 'K':
        return roster.k;
      case 'DEF':
        return roster.def;
      default:
        return 0;
    }
  }

  /**
   * Assign tiers to players based on their VBD scores
   */
  private assignTiers(): void {
    const positions = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];
    
    positions.forEach(position => {
      const positionPlayers = this.playerDatabase
        .filter(p => p.position === position)
        .sort((a, b) => {
          const aValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(a.id)) || 0;
          const bValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(b.id)) || 0;
          return bValue - aValue;
        });
      
      // Create tiers based on value gaps
      let currentTier = 1;
      let previousValue = 0;
      
      positionPlayers.forEach((player, index) => {
        const value = this.valueBasedDrafting.get(this.getPlayerIdAsString(player.id)) || 0;
        
        // First player is always tier 1
        if (index === 0) {
          this.playerTiers.set(this.getPlayerIdAsString(player.id), currentTier);
          previousValue = value;
          return;
        }
        
        // Check for significant value drop to create a new tier
        const valueDrop = previousValue - value;
        const percentDrop = valueDrop / previousValue;
        
        // Create a new tier if there's a significant drop in value
        if (percentDrop > 0.15) {
          currentTier++;
        }
        
        this.playerTiers.set(this.getPlayerIdAsString(player.id), currentTier);
        previousValue = value;
      });
    });
  }

  /**
   * Get draft recommendations based on current draft state
   */
  public getDraftRecommendations(
    draftState: DraftState,
    recommendationCount: number = 5
  ): DraftRecommendation[] {
    const { availablePlayers, userRoster, nextUserPicks } = draftState;
    const nextPick = nextUserPicks[0] || draftState.currentPick;
    
    // Analyze roster needs
    const rosterNeeds = this.analyzeRosterNeeds(userRoster);
    
    // Score available players
    const scoredPlayers = availablePlayers.map(player => {
      const playerValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(player.id)) || 0;
      const positionalNeed = rosterNeeds.get(player.position) || 0;
      const tierValue = this.calculateTierValue(player, availablePlayers);
      const adpValue = this.calculateADPValue(player, nextPick);
      
      const totalValue = playerValue + positionalNeed + tierValue + adpValue;
      
      return {
        player,
        value: totalValue,
        positionalNeed,
        playerValue,
        tierValue,
        adpValue
      };
    });
    
    // Sort by total value and take top recommendations
    const topPlayers = scoredPlayers
      .sort((a, b) => b.value - a.value)
      .slice(0, recommendationCount);
    
    // Generate recommendations with reasoning
    return topPlayers.map(scored => ({
      player: scored.player,
      reasoning: this.generateReasoning(scored, rosterNeeds, draftState),
      value: scored.value,
      positionalNeed: scored.positionalNeed,
      tier: this.playerTiers.get(this.getPlayerIdAsString(scored.player.id)) || 999,
      alternativePicks: this.getAlternativePicks(scored.player, availablePlayers),
      riskLevel: this.assessRiskLevel(scored.player)
    }));
  }

  /**
   * Analyze what positions the user needs most
   */
  private analyzeRosterNeeds(userRoster: Player[]): Map<string, number> {
    const needs = new Map<string, number>();
    const roster = this.leagueSettings.rosterPositions;
    
    // Count current roster by position
    const rosterCounts = new Map<string, number>();
    userRoster.forEach(player => {
      const count = rosterCounts.get(player.position) || 0;
      rosterCounts.set(player.position, count + 1);
    });
    
    // Calculate needs based on required starters
    needs.set('QB', Math.max(0, (roster.qb - (rosterCounts.get('QB') || 0)) * 10));
    needs.set('RB', Math.max(0, (roster.rb - (rosterCounts.get('RB') || 0)) * 8));
    needs.set('WR', Math.max(0, (roster.wr - (rosterCounts.get('WR') || 0)) * 8));
    needs.set('TE', Math.max(0, (roster.te - (rosterCounts.get('TE') || 0)) * 6));
    needs.set('K', Math.max(0, (roster.k - (rosterCounts.get('K') || 0)) * 2));
    needs.set('DEF', Math.max(0, (roster.def - (rosterCounts.get('DEF') || 0)) * 2));
    
    // Add flex considerations
    const flexEligibleCount = (rosterCounts.get('RB') || 0) + 
                             (rosterCounts.get('WR') || 0) + 
                             (rosterCounts.get('TE') || 0);
    const flexNeed = Math.max(0, this.leagueSettings.rosterPositions.flex - 
                             Math.max(0, flexEligibleCount - roster.rb - roster.wr - roster.te));
    
    if (flexNeed > 0) {
      needs.set('RB', (needs.get('RB') || 0) + flexNeed * 4);
      needs.set('WR', (needs.get('WR') || 0) + flexNeed * 4);
      needs.set('TE', (needs.get('TE') || 0) + flexNeed * 2);
    }
    
    return needs;
  }

  /**
   * Calculate tier-based value (prefer top players in their tier)
   */
  private calculateTierValue(player: Player, availablePlayers: Player[]): number {
    const playerTier = this.playerTiers.get(this.getPlayerIdAsString(player.id)) || 0;
    
    const sameTierPlayers = availablePlayers.filter(p => 
      p.position === player.position && 
      (this.playerTiers.get(this.getPlayerIdAsString(p.id)) || 0) === playerTier
    );
    
    const playerRankInTier = sameTierPlayers
      .sort((a, b) => {
        const aValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(a.id)) || 0;
        const bValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(b.id)) || 0;
        return bValue - aValue;
      })
      .findIndex(p => p.id === player.id);
    
    return Math.max(0, 10 - playerRankInTier);
  }

  /**
   * Calculate ADP-based value (draft value relative to current pick)
   */
  private calculateADPValue(player: Player, currentPick: number): number {
    // Use a simple ADP approximation based on player value ranking
    const playerRank = this.playerDatabase
      .sort((a, b) => {
        const aValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(a.id)) || 0;
        const bValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(b.id)) || 0;
        return bValue - aValue;
      })
      .findIndex(p => p.id === player.id) + 1;
    
    const adpDifference = playerRank - currentPick;
    
    if (adpDifference > 10) return -5; // Reaching too early
    if (adpDifference > 0) return 0;   // Slight reach
    if (adpDifference > -10) return 5;  // Good value
    return 10; // Excellent value
  }

  /**
   * Generate human-readable reasoning for recommendation
   */
  private generateReasoning(
    scored: any, 
    rosterNeeds: Map<string, number>, 
    draftState: DraftState
  ): string {
    const player = scored.player;
    const need = rosterNeeds.get(player.position) || 0;
    const nextPick = draftState.nextUserPicks[0] || draftState.currentPick;
    const playerRank = this.playerDatabase
      .sort((a, b) => {
        const aValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(a.id)) || 0;
        const bValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(b.id)) || 0;
        return bValue - aValue;
      })
      .findIndex(p => p.id === player.id) + 1;
    const adpDiff = playerRank - nextPick;
    
    let reasoning = `${player.name} (${player.position})`;
    
    if (need > 5) {
      reasoning += ` addresses a critical positional need`;
    } else if (need > 0) {
      reasoning += ` fills a roster need`;
    } else {
      reasoning += ` provides excellent value`;
    }
    
    if (adpDiff < -10) {
      reasoning += ` and is a major value at this draft position`;
    } else if (adpDiff < 0) {
      reasoning += ` and is a good value at this draft position`;
    } else if (adpDiff > 10) {
      reasoning += ` but may be a reach at this draft position`;
    }
    
    // Add tier information
    const tier = this.playerTiers.get(this.getPlayerIdAsString(player.id)) || 0;
    reasoning += `. Tier ${tier} ${player.position}.`;
    
    // Add context about player's projected points
    reasoning += ` Projected for ${player.projection} points.`;
    
    return reasoning;
  }

  /**
   * Get alternative picks for a selected player
   */
  private getAlternativePicks(selectedPlayer: Player, availablePlayers: Player[]): Player[] {
    const playerValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(selectedPlayer.id)) || 0;
    
    // Find players of similar value but different positions
    return availablePlayers
      .filter(p => {
        const pValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(p.id)) || 0;
        return p.id !== selectedPlayer.id && 
               Math.abs(pValue - playerValue) < playerValue * 0.15 &&
               p.position !== selectedPlayer.position;
      })
      .sort((a, b) => {
        const aValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(a.id)) || 0;
        const bValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(b.id)) || 0;
        return bValue - aValue;
      })
      .slice(0, 3);
  }

  /**
   * Assess risk level based on player characteristics
   */
  private assessRiskLevel(player: Player): 'low' | 'medium' | 'high' {
    let riskFactors = 0;
    
    // Age-based risk
    if (player.age) {
      if (player.age > 30) riskFactors += 1;
      if (player.age > 33) riskFactors += 1;
    }
    
    // Injury risk if available
    if (player.injuryRisk && player.injuryRisk > 0.5) riskFactors += 1;
    
    // Position-based risk
    if (player.position === 'RB') riskFactors += 1; // RBs have higher injury rates
    
    // Consistency-based risk (if available)
    if (player.consistencyRating !== undefined) {
      if (player.consistencyRating < 0.5) riskFactors += 1;
    }
    
    // Determine risk level based on total risk factors
    if (riskFactors >= 3) return 'high';
    if (riskFactors >= 1) return 'medium';
    return 'low';
  }

  /**
   * Update league settings and recalculate values
   */
  public updateLeagueSettings(newSettings: LeagueSettings): void {
    this.leagueSettings = newSettings;
    // Removed reference to scoringRules as it's not used
    this.calculateVBD();
    this.assignTiers();
  }

  /**
   * Get positional rankings for a specific position
   */
  public getPositionalRankings(position: string): Player[] {
    return this.playerDatabase
      .filter(p => p.position === position)
      .sort((a, b) => {
        const aValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(a.id)) || 0;
        const bValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(b.id)) || 0;
        return bValue - aValue;
      });
  }

  /**
   * Get optimal draft strategy based on draft position
   */
  public getDraftStrategy(draftPosition: number): DraftStrategy {
    // Early pick strategies (1-4)
    if (draftPosition <= 4) {
      return {
        name: "Elite RB Strategy",
        description: "Focus on securing an elite RB early, then target top WRs in rounds 2-3, and value QB/TE in middle rounds",
        earlyRoundFocus: ["RB", "WR", "WR"],
        midRoundFocus: ["QB", "TE", "RB", "WR"],
        lateRoundFocus: ["DEF", "K", "High-upside RB/WR"],
        riskTolerance: "low",
        positionPriorities: {
          "QB": 3,
          "RB": 5,
          "WR": 4,
          "TE": 3,
          "K": 1,
          "DEF": 1
        }
      };
    }
    // Middle pick strategies (5-8)
    else if (draftPosition <= 8) {
      return {
        name: "Balanced Approach",
        description: "Take best player available early with slight RB/WR preference, then focus on value at QB/TE in middle rounds",
        earlyRoundFocus: ["RB/WR", "WR/RB", "TE/RB/WR"],
        midRoundFocus: ["QB", "RB", "WR"],
        lateRoundFocus: ["DEF", "K", "High-upside RB/WR"],
        riskTolerance: "medium",
        positionPriorities: {
          "QB": 3,
          "RB": 4,
          "WR": 4,
          "TE": 3,
          "K": 1,
          "DEF": 1
        }
      };
    }
    // Late pick strategies (9-12)
    else {
      return {
        name: "Modified Zero RB",
        description: "Target elite WRs and possibly an elite TE/QB early, then focus on high-upside RBs in middle rounds",
        earlyRoundFocus: ["WR", "WR/TE", "QB/RB"],
        midRoundFocus: ["RB", "RB", "WR", "TE"],
        lateRoundFocus: ["RB", "WR", "DEF", "K"],
        riskTolerance: "high",
        positionPriorities: {
          "QB": 3,
          "RB": 3,
          "WR": 5,
          "TE": 4,
          "K": 1,
          "DEF": 1
        }
      };
    }
  }
  
  /**
   * Run a mock draft simulation
   */
  public runMockDraft(draftPosition: number, totalTeams: number): MockDraftResult {
    // Create a copy of the player database for the mock draft
    const availablePlayers = [...this.playerDatabase];
    
    // Initialize team rosters
    const teams: { teamId: number; roster: Player[]; projectedPoints: number }[] = [];
    for (let i = 0; i < totalTeams; i++) {
      teams.push({ teamId: i, roster: [], projectedPoints: 0 });
    }
    
    // Run the draft
    const totalRounds = this.calculateTotalRosterSpots();
    
    for (let round = 1; round <= totalRounds; round++) {
      for (let pick = 1; pick <= totalTeams; pick++) {
        // Calculate the team picking based on snake draft
        let teamId;
        if (round % 2 === 1) {
          // Odd rounds go 1 to totalTeams
          teamId = pick - 1;
        } else {
          // Even rounds go totalTeams to 1
          teamId = totalTeams - pick;
        }
        
        // If this is the user's team, skip (user will make their own picks)
        if (teamId === draftPosition - 1) {
          // Remove a player to simulate the user's pick
          if (availablePlayers.length > 0) {
            availablePlayers.shift();
          }
          continue;
        }
        
        // AI team makes a pick
        const bestPlayer = this.getBestPlayerForTeam(availablePlayers, teams[teamId].roster);
        
        if (bestPlayer) {
          // Add player to team roster
          teams[teamId].roster.push(bestPlayer);
          teams[teamId].projectedPoints += bestPlayer.projection || 0;
          
          // Remove player from available players
          const index = availablePlayers.findIndex(p => p.id === bestPlayer.id);
          if (index !== -1) {
            availablePlayers.splice(index, 1);
          }
        }
      }
    }
    
    return { teams };
  }
  
  /**
   * Calculate total roster spots based on league settings
   */
  private calculateTotalRosterSpots(): number {
    const { rosterPositions, benchSize } = this.leagueSettings;
    
    return rosterPositions.qb + 
           rosterPositions.rb + 
           rosterPositions.wr + 
           rosterPositions.te + 
           rosterPositions.k + 
           rosterPositions.def + 
           rosterPositions.flex + 
           (rosterPositions.superFlex || 0) + 
           benchSize;
  }
  
  /**
   * Get the best player for a team based on their current roster
   */
  private getBestPlayerForTeam(availablePlayers: Player[], teamRoster: Player[]): Player | null {
    if (availablePlayers.length === 0) return null;
    
    // Analyze team needs
    const rosterNeeds = this.analyzeRosterNeeds(teamRoster);
    
    // Score available players
    const scoredPlayers = availablePlayers.map(player => {
      const playerValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(player.id)) || 0;
      const positionalNeed = rosterNeeds.get(player.position) || 0;
      
      // Simple scoring for AI teams
      return {
        player,
        score: playerValue + (positionalNeed * 0.5)
      };
    });
    
    // Sort and return the best player
    scoredPlayers.sort((a, b) => b.score - a.score);
    return scoredPlayers[0]?.player || null;
  }
  
  /**
   * Export draft board to CSV format
   */
  public exportDraftBoard(): string {
    const headers = ['Rank', 'Player', 'Position', 'Team', 'Tier', 'Value', 'Projection'];
    
    const rows = this.playerDatabase
      .sort((a, b) => {
        const aValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(a.id)) || 0;
        const bValue = this.valueBasedDrafting.get(this.getPlayerIdAsString(b.id)) || 0;
        return bValue - aValue;
      })
      .map((player, index) => [
        (index + 1).toString(),
        player.name,
        player.position,
        player.team,
        (this.playerTiers.get(this.getPlayerIdAsString(player.id)) || '-').toString(),
        (this.valueBasedDrafting.get(this.getPlayerIdAsString(player.id)) || 0).toFixed(2),
        (player.projection || 0).toString()
      ]);
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  }
}

/**
 * Utility functions for draft assistance
 */
export class DraftUtilities {
  /**
   * Convert a standard league settings object to our internal format
   */
  static convertStandardSettings(
    teams: number, 
    scoringFormat: 'standard' | 'ppr' | 'half-ppr',
    qbs: number = 1,
    rbs: number = 2,
    wrs: number = 2,
    tes: number = 1,
    flex: number = 1,
    superflex: number = 0,
    kickers: number = 1,
    defenses: number = 1,
    benchSize: number = 6
  ): LeagueSettings {
    return {
      teams,
      scoringFormat,
      rosterPositions: {
        qb: qbs,
        rb: rbs,
        wr: wrs,
        te: tes,
        k: kickers,
        def: defenses,
        flex,
        superFlex: superflex
      },
      benchSize,
      flexPositions: {
        rbWrTe: flex,
        rbWr: 0,
        wrTe: 0,
        qbRbWrTe: superflex
      },
      customScoring: DEFAULT_SCORING[scoringFormat]
    };
  }
  
  /**
   * Get default draft strategies
   */
  static getDefaultStrategies(): DraftStrategy[] {
    return [
      {
        name: "Zero RB",
        description: "Focus on elite WRs, QB, and TE early, then target high-upside RBs in middle to late rounds",
        earlyRoundFocus: ["WR", "WR", "TE/QB"],
        midRoundFocus: ["WR", "RB", "RB"],
        lateRoundFocus: ["RB", "RB", "DEF", "K"],
        riskTolerance: "high",
        positionPriorities: {
          "QB": 3,
          "RB": 2,
          "WR": 5,
          "TE": 4,
          "K": 1,
          "DEF": 1
        }
      },
      {
        name: "Hero RB",
        description: "Secure one elite RB early, then focus on WRs before targeting RB depth later",
        earlyRoundFocus: ["RB", "WR", "WR"],
        midRoundFocus: ["TE", "QB", "WR"],
        lateRoundFocus: ["RB", "RB", "DEF", "K"],
        riskTolerance: "medium",
        positionPriorities: {
          "QB": 3,
          "RB": 4,
          "WR": 4,
          "TE": 3,
          "K": 1,
          "DEF": 1
        }
      },
      {
        name: "Robust RB",
        description: "Load up on RBs early to secure positional scarcity, then target value at WR/TE/QB",
        earlyRoundFocus: ["RB", "RB", "RB/WR"],
        midRoundFocus: ["WR", "WR", "TE"],
        lateRoundFocus: ["QB", "WR", "DEF", "K"],
        riskTolerance: "low",
        positionPriorities: {
          "QB": 2,
          "RB": 5,
          "WR": 3,
          "TE": 3,
          "K": 1,
          "DEF": 1
        }
      },
      {
        name: "Anchor TE",
        description: "Secure an elite TE early for positional advantage, then balance RB/WR through middle rounds",
        earlyRoundFocus: ["RB/WR", "TE", "RB/WR"],
        midRoundFocus: ["RB", "WR", "QB"],
        lateRoundFocus: ["RB", "WR", "DEF", "K"],
        riskTolerance: "medium",
        positionPriorities: {
          "QB": 3,
          "RB": 4,
          "WR": 4,
          "TE": 5,
          "K": 1,
          "DEF": 1
        }
      }
    ];
  }
}
